import React, { useEffect, useState } from 'react';
import {
  Menu,
  X,
  ArrowRight,
  Shield,
  Scale,
  Activity,
  Clock,
  CheckCircle2,
  Building2,
  Landmark,
  Briefcase
} from 'lucide-react';
import toast from 'react-hot-toast';
import { BRAND } from '../../brand';
import BrandLockup from '../BrandLockup';
import AuthenticationGate from './AuthenticationGate';
import SampleBriefingModal from './SampleBriefingModal';
import SPECIMEN from '../../content/specimenOpinion';
import '../../styles/landing.css';

const USE_CASES = {
  procurement: {
    label: 'Procurement',
    icon: Building2,
    points: [
      'Supplier qualification and onboarding screens',
      'Contract renewal and commercial-term decisions',
      'Critical and sole-source supplier review',
      'Diversification planning when financial risk rises'
    ]
  },
  treasury: {
    label: 'Treasury',
    icon: Landmark,
    points: [
      'Counterparty creditworthiness and exposure review',
      'Payment-term and open-account decisions',
      'Bank and financial-institution monitoring',
      'Evidence-based re-screen cadence'
    ]
  },
  finance: {
    label: 'Finance & Risk',
    icon: Briefcase,
    points: [
      'Subsidiary financial-health screens',
      'M&A and target triage screens',
      'An Opinion you can file, with the methodology disclosed',
      'A re-screen cadence tied to the evidence'
    ]
  }
};

const LENSES = [
  { value: 'vendor', label: 'Vendor / supplier' },
  { value: 'subsidiary', label: 'Subsidiary' },
  { value: 'ma_private', label: 'M&A triage' },
  { value: 'investor', label: 'Investor' }
];

const LandingPage = ({ onAnalyze, error }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [email, setEmail] = useState('');
  const [lens, setLens] = useState('vendor');
  const [counterpartyRole, setCounterpartyRole] = useState('supplier');
  const [useCaseTab, setUseCaseTab] = useState('procurement');
  const [sampleOpen, setSampleOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authReason, setAuthReason] = useState('');

  useEffect(() => {
    // Placeholder for analytics: landing_view
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  const openAuth = (reason) => {
    setAuthReason(reason);
    setAuthOpen(true);
  };

  const startAnalysis = (e) => {
    e?.preventDefault();
    const companies = query
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!companies.length) {
      toast.error('Enter a company name or ticker to begin');
      document.getElementById('analyze-query')?.focus();
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      toast.error('Enter your work email — your first Opinion is free and delivered as a PDF');
      document.getElementById('analyze-email')?.focus();
      return;
    }
    if (companies.length > 1) {
      toast.error('Enter one company. Run the next Opinion after this one finishes.');
      return;
    }
    onAnalyze(companies, lens, {
      deliveryEmail: email.trim(),
      counterpartyRole: lens === 'vendor' || lens === 'subsidiary' ? counterpartyRole : 'supplier'
    });
  };

  const activeUse = USE_CASES[useCaseTab];
  const UseIcon = activeUse.icon;

  return (
    <div className="mk-page">
      <a href="#analyze" className="mk-skip">
        Skip to analysis
      </a>

      <header className="mk-nav">
        <div className="mk-nav-inner">
          <BrandLockup onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />

          <nav className="mk-nav-links" aria-label="Primary">
            <button type="button" onClick={() => scrollTo('product')}>
              Product
            </button>
            <button type="button" onClick={() => scrollTo('solutions')}>
              Solutions
            </button>
            <button type="button" onClick={() => scrollTo('methodology')}>
              Methodology
            </button>
            <button type="button" onClick={() => setSampleOpen(true)}>
              Sample Opinion
            </button>
          </nav>

          <div className="mk-nav-actions">
            <button type="button" className="mk-linkish" onClick={() => openAuth()}>
              Sign in
            </button>
            <button type="button" className="mk-btn mk-btn-primary mk-btn-sm" onClick={() => scrollTo('analyze')}>
              Analyze a company
            </button>
            <button
              type="button"
              className="mk-nav-burger"
              aria-expanded={menuOpen}
              aria-label="Open menu"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="mk-nav-mobile" role="dialog" aria-label="Mobile menu">
            <button type="button" onClick={() => scrollTo('product')}>
              Product
            </button>
            <button type="button" onClick={() => scrollTo('solutions')}>
              Solutions
            </button>
            <button type="button" onClick={() => scrollTo('methodology')}>
              Methodology
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setSampleOpen(true);
              }}
            >
              Sample Opinion
            </button>
            <button type="button" className="mk-btn mk-btn-primary" onClick={() => scrollTo('analyze')}>
              Analyze a company
            </button>
          </div>
        )}
      </header>

      <main>
        <section className="mk-hero" id="product">
          <div className="mk-hero-grid">
            <div className="mk-hero-copy">
              <p className="mk-eyebrow">{BRAND.descriptor.toUpperCase()}</p>
              <h1>{BRAND.tagline}</h1>
              <p className="mk-lede">
                A dated Opinion on one counterparty: the condition, what follows from the figures, and when to look again.
              </p>
              <div className="mk-hero-ctas">
                <button type="button" className="mk-btn mk-btn-primary" onClick={() => scrollTo('analyze')}>
                  Analyze a company
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
                <button type="button" className="mk-btn mk-btn-ghost" onClick={() => setSampleOpen(true)}>
                  View sample Opinion
                </button>
              </div>
              <p className="mk-trust-line">{BRAND.supportLine}</p>
            </div>

            <aside className="mk-preview" aria-label="Illustrative sample Opinion">
              <div className="mk-preview-badge">Illustrative sample</div>
              <div className="mk-preview-topline">
                <div>
                  <h2>{SPECIMEN.company}</h2>
                  <p>Opinion No. {SPECIMEN.opinionNumber} · {SPECIMEN.basis}</p>
                </div>
                <span className="mk-pill mk-pill-low">{SPECIMEN.condition}</span>
              </div>
              <div className="mk-opinion">
                {(SPECIMEN.opinionRows || []).map((row) => (
                  <p key={row.text}>{row.text}</p>
                ))}
              </div>
              <div className="mk-figures">
                {(SPECIMEN.figureRows || []).map((row) => (
                  <div key={row.label}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
              <div className="mk-preview-actions">
                <div>
                  <span>Procurement</span>
                  <p>{SPECIMEN.procurement}</p>
                </div>
                <div>
                  <span>Treasury</span>
                  <p>{SPECIMEN.treasury}</p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="mk-analyze" id="analyze">
          <div className="mk-analyze-inner">
            <div className="mk-analyze-head">
              <h2>Start with one counterparty</h2>
              <p>
                Enter a public company and your email. Your first Opinion is free — no card — and arrives as a PDF you can file.
              </p>
            </div>
            <form className="mk-search" onSubmit={startAnalysis}>
              <div className="mk-search-row">
                <label className="mk-sr-only" htmlFor="analyze-query">
                  Company name or ticker
                </label>
                <input
                  id="analyze-query"
                  className="mk-search-input"
                  placeholder="e.g. AAPL, Toyota, Caterpillar"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoComplete="off"
                />
                <button type="submit" className="mk-btn mk-btn-primary">
                  Analyze
                </button>
              </div>
              <div className="mk-search-row" style={{ marginTop: '0.65rem' }}>
                <label className="mk-sr-only" htmlFor="analyze-email">
                  Work email for delivery
                </label>
                <input
                  id="analyze-email"
                  type="email"
                  className="mk-search-input"
                  placeholder="Work email — PDF delivery, first Opinion free"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <fieldset className="mk-lenses">
                <legend className="mk-sr-only">Analysis lens</legend>
                {LENSES.map((l) => (
                  <label key={l.value} className={lens === l.value ? 'is-active' : ''}>
                    <input
                      type="radio"
                      name="lens"
                      value={l.value}
                      checked={lens === l.value}
                      onChange={() => setLens(l.value)}
                    />
                    {l.label}
                  </label>
                ))}
              </fieldset>
              {(lens === 'vendor' || lens === 'subsidiary') && (
                <fieldset className="mk-lenses">
                  <legend className="mk-lens-legend">This counterparty is</legend>
                  <label className={counterpartyRole === 'supplier' ? 'is-active' : ''}>
                    <input
                      type="radio"
                      name="role"
                      value="supplier"
                      checked={counterpartyRole === 'supplier'}
                      onChange={() => setCounterpartyRole('supplier')}
                    />
                    Supplier — you buy from them
                  </label>
                  <label className={counterpartyRole === 'customer' ? 'is-active' : ''}>
                    <input
                      type="radio"
                      name="role"
                      value="customer"
                      checked={counterpartyRole === 'customer'}
                      onChange={() => setCounterpartyRole('customer')}
                    />
                    Customer — you sell to them
                  </label>
                </fieldset>
              )}
              {error ? <p className="mk-form-error" role="alert">{error}</p> : null}
            </form>
          </div>
        </section>

        <section className="mk-outcomes" aria-labelledby="outcomes-title">
          <h2 id="outcomes-title" className="mk-sr-only">
            What you receive
          </h2>
          <div className="mk-outcomes-grid">
            <article>
              <Shield size={20} aria-hidden="true" />
              <h3>Financial health and distress assessment</h3>
              <p>Synthesize Altman, Ohlson, liquidity, leverage, profitability, cash flow, and multi-year trend into one health view.</p>
            </article>
            <article>
              <Scale size={20} aria-hidden="true" />
              <h3>Executive business decision</h3>
              <p>Translate complex financial evidence into a defensible commercial decision a credit committee can stand behind.</p>
            </article>
            <article>
              <Activity size={20} aria-hidden="true" />
              <h3>Procurement and Treasury actions</h3>
              <p>Aligned next steps for sourcing governance and credit terms, proportional to overall risk, not a single ratio.</p>
            </article>
            <article>
              <Clock size={20} aria-hidden="true" />
              <h3>Monitoring cadence</h3>
              <p>Evidence-based review frequency and early-warning indicators so follow-up matches the risk, not calendar habit.</p>
            </article>
          </div>
        </section>

        <section className="mk-sample-cta" id="sample">
          <div className="mk-sample-cta-inner">
            <div>
              <p className="mk-eyebrow">Sample Opinion</p>
              <h2>See how the figures become a Position</h2>
              <p>
                The opinion, the figures it rests on, and what would change it.
              </p>
              <button type="button" className="mk-btn mk-btn-primary" onClick={() => setSampleOpen(true)}>
                Read the sample Opinion
              </button>
            </div>
            <ul className="mk-checklist">
              <li>
                <CheckCircle2 size={16} aria-hidden="true" /> Condition, then Position
              </li>
              <li>
                <CheckCircle2 size={16} aria-hidden="true" /> Evidence tied to a sourced figure
              </li>
              <li>
                <CheckCircle2 size={16} aria-hidden="true" /> What would change the Opinion
              </li>
              <li>
                <CheckCircle2 size={16} aria-hidden="true" /> Procurement and Treasury readings
              </li>
            </ul>
          </div>
        </section>

        <section className="mk-solutions" id="solutions">
          <div className="mk-section-head">
            <p className="mk-eyebrow">By function</p>
            <h2>Built for the teams that own counterparty risk</h2>
          </div>
          <div className="mk-tabs" role="tablist" aria-label="Use cases">
            {Object.entries(USE_CASES).map(([key, val]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={useCaseTab === key}
                className={useCaseTab === key ? 'is-active' : ''}
                onClick={() => setUseCaseTab(key)}
              >
                {val.label}
              </button>
            ))}
          </div>
          <div className="mk-tab-panel" role="tabpanel">
            <div className="mk-tab-icon">
              <UseIcon size={22} aria-hidden="true" />
            </div>
            <h3>{activeUse.label}</h3>
            <ul>
              {activeUse.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mk-compare">
          <div className="mk-section-head">
            <p className="mk-eyebrow">Differentiation</p>
            <h2>Beyond scores and ratio dumps</h2>
          </div>
          <div className="mk-compare-grid">
            <div>
              <h3>Traditional analysis</h3>
              <ul>
                <li>Produces scores and ratios</li>
                <li>Requires manual interpretation</li>
                <li>Often separates metrics from commercial context</li>
                <li>Limited functional guidance</li>
                <li>Decisions can vary by analyst</li>
              </ul>
            </div>
            <div className="mk-compare-emphasis">
              <h3>{BRAND.productName}</h3>
              <ul>
                <li>Synthesizes all available financial indicators</li>
                <li>Reconciles conflicting signals explicitly</li>
                <li>Produces aligned Procurement and Treasury actions</li>
                <li>Sets an evidence-based monitoring cadence</li>
                <li>Delivers an executive-ready briefing and PDF</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mk-steps">
          <div className="mk-section-head">
            <p className="mk-eyebrow">How it works</p>
            <h2>From entity to decision in three steps</h2>
          </div>
          <ol className="mk-steps-grid">
            <li>
              <span>01</span>
              <h3>Select a company</h3>
              <p>Identify the legal entity and choose the analysis lens that matches your decision.</p>
            </li>
            <li>
              <span>02</span>
              <h3>Review the evidence</h3>
              <p>Models, ratios, trends, and data confidence are disclosed, not hidden behind a black box.</p>
            </li>
            <li>
              <span>03</span>
              <h3>Act with a briefing</h3>
              <p>Receive financial health, commercial implications, recommended actions, and monitoring cadence.</p>
            </li>
          </ol>
        </section>

        <section className="mk-method" id="methodology">
          <div className="mk-section-head">
            <p className="mk-eyebrow">Methodology and trust</p>
            <h2>Decision support you can inspect</h2>
          </div>
          <div className="mk-method-grid">
            <p>
              Analysis draws on multiple financial indicators. No single ratio determines the recommendation. Quantitative
              models and narrative interpretation are separated and labeled. Data confidence, reporting periods, and
              source dates travel with every result.
            </p>
            <ul>
              <li>Altman Z-Score variants disclosed by company type and equity basis</li>
              <li>Independent Ohlson O-Score as a second distress read</li>
              <li>Limitations and incomplete data communicated openly</li>
              <li>Output is decision support, not a credit rating or investment advice</li>
            </ul>
          </div>
          <div className="mk-method-actions">
            <button type="button" className="mk-btn mk-btn-ghost" onClick={() => scrollTo('disclaimer')}>
              Read limitations
            </button>
            <button type="button" className="mk-btn mk-btn-ghost" onClick={() => setSampleOpen(true)}>
              View sample Opinion
            </button>
          </div>
        </section>

        <section className="mk-final">
          <h2>Turn financial evidence into a clear commercial decision</h2>
          <p>Run one public company. The first Opinion is free and arrives as a PDF you can file.</p>
          <div className="mk-hero-ctas">
            <button type="button" className="mk-btn mk-btn-primary" onClick={() => scrollTo('analyze')}>
              Analyze a company
            </button>
          </div>
        </section>
      </main>

      <footer className="mk-footer" id="disclaimer">
        <div className="mk-footer-grid">
          <div>
            <strong>{BRAND.productName}</strong>
            <p>{BRAND.descriptor}</p>
            <p className="mk-footer-legal">
              Decision-support only. Not legal, tax, accounting, investment, or credit-rating advice. Users must verify
              figures and apply output to their policies.
            </p>
          </div>
          <div>
            <span className="mk-kicker">Product</span>
            <button type="button" onClick={() => scrollTo('product')}>
              Overview
            </button>
            <button type="button" onClick={() => scrollTo('analyze')}>
              Analyze
            </button>
            <button type="button" onClick={() => setSampleOpen(true)}>
              Sample Opinion
            </button>
          </div>
          <div>
            <span className="mk-kicker">Company</span>
            <button type="button" onClick={() => scrollTo('methodology')}>
              Methodology
            </button>
            <a href={`mailto:${BRAND.contactEmail}`}>Contact</a>
            <button type="button" onClick={() => openAuth()}>
              Sign in
            </button>
          </div>
          <div>
            <span className="mk-kicker">Legal</span>
            <a href={`mailto:${BRAND.contactEmail}?subject=Privacy%20inquiry`}>Privacy</a>
            <a href={`mailto:${BRAND.contactEmail}?subject=Terms%20inquiry`}>Terms</a>
            <button type="button" onClick={() => scrollTo('disclaimer')}>
              Disclaimer
            </button>
          </div>
        </div>
        <p className="mk-footer-copy">{BRAND.legalCopyright}</p>
      </footer>

      <SampleBriefingModal open={sampleOpen} onClose={() => setSampleOpen(false)} />
      <AuthenticationGate
        open={authOpen}
        reason={authReason}
        onClose={() => setAuthOpen(false)}
      />
    </div>
  );
};

export default LandingPage;
